// Card 49, the picket line (R2657, ui-map-interface.md §17.3.49,
// v2-the-deep.md §11.3 and §13 "Defences"; mockup round 031 card 49).
//
// A defended harbour closed its water with a boom, a chain of floats
// between two points; the current chart marks the same closure as a row of
// obstruction symbols. Here the boom runs between two own fixed listeners
// whose hearing rings touch: an anchored Hark at the sonar of the
// catalog (`SeaObjectSpec.sonar_px`) and a settled Sentinel at the range
// the wire states for it. The line says "nothing crosses here unheard". It
// is a hearing line and not a wall, and the words say so.
//
// The booms are the shortest links that join the listeners whose rings
// touch (a spanning tree, shortest first, in source order), so a row of
// buoys along a shipping lane draws one chain and never a mesh.
//
// **WITH THE HARKS.** The Hark is the listener of v2 phase 6,
// so the piece draws while the picture holds an own anchored Hark.
// A world with `sapper` off holds none, and draws the chart of today.
//
// **COST.** A live piece: one dashed segment and a few floats a boom.

import type { FrameInfo } from '../../layer';
import type { ChartContext, ChartFleet, ChartSinks, Piece } from '../context';
import { chart as S } from '../../../gen/strings';
import { AnchorState, SeaObjectKind, ShipClass } from '../../../gen/proto/aquaphobia';
import { CHART_INK, faceOf, inkOf } from './shared';
import { ownPicket } from './listening-watch';

/** One fixed listener: its point and its hearing range, world px. */
export interface Listener { x: number; y: number; r: number }

/** One boom, between two listeners. */
export interface Boom { a: Listener; b: Listener }

// The scratch of `boomsInto` and `listenersInto`: the touching pairs in
// parallel arrays, the order they join in, the union-find roots and the
// settled Sentinels in id order. Kept, so a frame allocates no pair.
const PAIR_I: number[] = [];
const PAIR_J: number[] = [];
const PAIR_D: number[] = [];
const ORDER: number[] = [];
const ROOT: number[] = [];
const SENTINELS: ChartFleet[] = [];

function find(i: number): number {
  while (ROOT[i] !== i) i = ROOT[i] = ROOT[ROOT[i]];
  return i;
}

/**
 * The booms of the first `n` listeners of `ls`: a spanning tree over the
 * pairs whose rings touch, shortest first. Ties keep the source order, so
 * one picture always draws one chain. The booms go into `out` from slot 0,
 * a slot already there is rewritten, and the count comes back.
 */
export function boomsInto(ls: readonly Listener[], n: number, out: Boom[]): number {
  let np = 0;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const d = Math.hypot(ls[j].x - ls[i].x, ls[j].y - ls[i].y);
      if (!(d > 0 && d <= ls[i].r + ls[j].r)) continue;
      PAIR_I[np] = i; PAIR_J[np] = j; PAIR_D[np] = d;
      // The pairs come in (i, j) order, so a pair goes after every pair no
      // longer than it: the order of a sort by d, then i, then j.
      let k = np;
      while (k > 0 && PAIR_D[ORDER[k - 1]] > d) { ORDER[k] = ORDER[k - 1]; k--; }
      ORDER[k] = np++;
    }
  }
  for (let i = 0; i < n; i++) ROOT[i] = i;
  let nb = 0;
  for (let k = 0; k < np; k++) {
    const p = ORDER[k], a = find(PAIR_I[p]), b = find(PAIR_J[p]);
    if (a === b) continue;
    ROOT[a] = b;
    const la = ls[PAIR_I[p]], lb = ls[PAIR_J[p]], o = out[nb];
    if (o) { o.a = la; o.b = lb; } else out[nb] = { a: la, b: lb };
    nb++;
  }
  return nb;
}

/** The booms of a set of listeners, in a new list: `boomsInto` of them all. */
export function booms(ls: readonly Listener[]): Boom[] {
  const out: Boom[] = [];
  boomsInto(ls, ls.length, out);
  return out;
}

/** Listener slot `n` of `out` set to a point and a range; the next slot comes back. */
function putListener(out: Listener[], n: number, x: number, y: number, r: number): number {
  const l = out[n];
  if (l) { l.x = x; l.y = y; l.r = r; } else out[n] = { x, y, r };
  return n + 1;
}

/**
 * The own fixed listeners of the picture: the anchored Harks, then
 * the settled Sentinels, each in id order. They go into `out` from slot 0,
 * a slot already there is rewritten, and the count comes back.
 */
export function listenersInto(ctx: ChartContext, out: Listener[]): number {
  let n = 0;
  const buoyR = ctx.state.seaObject(SeaObjectKind.SEA_OBJECT_KIND_PICKET_BUOY)?.sonarPx ?? 0;
  for (const o of ctx.state.seaObjects.list) {
    if (ownPicket(o) && buoyR > 0) n = putListener(out, n, o.x, o.y, buoyR);
  }
  if (n === 0) return 0;
  const fleets = ctx.scene.fleets?.chartFleets();
  if (!fleets) return n;
  // The settled Sentinels, each put after every one of a lower id.
  let ns = 0;
  for (const f of fleets) {
    if (!(f.own && f.cls === ShipClass.SHIP_CLASS_SENTINEL && f.anchor === AnchorState.ANCHOR_STATE_SET && f.sonarPx > 0)) {
      continue;
    }
    let k = ns++;
    while (k > 0 && SENTINELS[k - 1].id > f.id) { SENTINELS[k] = SENTINELS[k - 1]; k--; }
    SENTINELS[k] = f;
  }
  for (let k = 0; k < ns; k++) {
    const f = SENTINELS[k];
    n = putListener(out, n, f.x, f.y, f.sonarPx);
  }
  return n;
}

/** The options of the boom's words. */
const CENTRE = { anchor: 'centre' } as const;

export class PicketLinePiece implements Piece {
  readonly id = 'picket_line' as const;
  readonly tier = 'screen' as const;
  readonly pack = 'live' as const;
  /** The listeners are today's: the film hides the line. */
  readonly film = 'hide' as const;
  readonly rebuildOn = [] as const;

  visible(ctx: ChartContext): boolean {
    return ctx.state.seaObjects.list.some(ownPicket);
  }

  /** The listeners and the booms of the frame in hand, kept. */
  private readonly ls: Listener[] = [];
  private readonly bs: Boom[] = [];

  rebuild(_ctx: ChartContext): void { /* the line is read each frame */ }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    const t = ctx.cfg.pieces.picket_line;
    const fig = t.fig;
    const r = t[ctx.reading];
    const hist = ctx.reading === 'historical';
    const ink = inkOf(r.ink, hist ? CHART_INK.pale : CHART_INK.magenta);
    const face = faceOf(ctx.reading);
    const nb = boomsInto(this.ls, listenersInto(ctx, this.ls), this.bs);
    for (let k = 0; k < nb; k++) {
      const { a, b } = this.bs[k];
      const dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy);
      if (!(L > 2 * fig.inset_px)) continue;
      const ux = dx / L, uy = dy / L;
      if (hist) {
        // The boom sags between its two ends, with a float each step.
        let px = a.x + ux * fig.inset_px, py = a.y + uy * fig.inset_px;
        for (let s = fig.inset_px + fig.sag_step_px; s <= L - fig.inset_px; s += fig.sag_step_px) {
          const sag = Math.sin((s / L) * Math.PI) * fig.sag_px;
          const x = a.x + ux * s - uy * sag, y = a.y + uy * s + ux * sag;
          sinks.segments.line(px, py, x, y, r.width, ink, r.alpha);
          px = x; py = y;
        }
        for (let s = fig.inset_px + fig.float_step_px; s < L - fig.inset_px; s += fig.float_step_px) {
          const sag = Math.sin((s / L) * Math.PI) * fig.sag_px;
          sinks.circles.disc(a.x + ux * s - uy * sag, a.y + uy * s + ux * sag, fig.float_px, ink, r.alpha);
        }
      } else {
        sinks.segments.line(a.x + ux * fig.inset_px, a.y + uy * fig.inset_px, b.x - ux * fig.inset_px,
          b.y - uy * fig.inset_px, r.width, ink, r.alpha, 0, r.dash[0], r.dash[1]);
        // The obstruction crosses of the current chart, along the boom.
        for (let s = fig.inset_px + fig.float_step_px; s < L - fig.inset_px; s += fig.float_step_px) {
          const x = a.x + ux * s, y = a.y + uy * s, k = fig.float_px;
          sinks.segments.line(x - k, y - k, x + k, y + k, r.width, ink, r.alpha);
          sinks.segments.line(x - k, y + k, x + k, y - k, r.width, ink, r.alpha);
        }
      }
      const reach = Math.round(Math.min(a.r, b.r));
      ctx.glyphs.word(sinks.quads, hist ? S.picket_line_historical : S.picket_line({ r: reach }),
        (a.x + b.x) / 2, (a.y + b.y) / 2 - fig.label_dy, face, t.text.main, ink, r.alpha, CENTRE);
    }
  }
}

export const picketLine = new PicketLinePiece();
